import * as React from "react";
import { useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CssBaseline from "@mui/material/CssBaseline";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ForgotPassword from "./ForgotPassword";
import { FacebookIcon, GoogleIcon } from "./CustomIcons";
import AppTheme from "../shared-theme/AppTheme";
import { CustomCard, CustomContainer } from "../shared-theme/BasicComponentTheme";
import { useNavigate } from "react-router-dom";

import McdcLogo from "../../assets/images/logo-mcdc.png";
import ClemsonLogo from "../../assets/images/logo-clemson.png";
import { useAuthManager } from "../../hooks/useAuthHook";
import Stack from "@mui/material/Stack";
import { Alert } from "@mui/material";

export default function SignIn(props: { disableCustomTheme?: boolean }) {

  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const [email, setEmail] = React.useState("");

  const [isEmailVerified, setIsEmailVerified] = React.useState(false);
  const [invalidCredentials, setInvalidCredentials] = React.useState(false);
  const [unknownError, setUnknownError] = React.useState(false);
  const [resentVerification, setResentVerification] = React.useState(false);

  const navigate = useNavigate();

  // Extract the signInMutation hook from useAuthManager
  const { useSignInMutation, useResendVerification, useForgotPassword } = useAuthManager();
  const { mutate: signIn, isLoading: isSigning, error, data } = useSignInMutation();
  const { mutate: resendEmailVerification, isLoading: isResending } = useResendVerification();
  const { mutate: forgotPassword } = useForgotPassword();

  const handleResendVerificationEmail = () => {

    const onSuccess = () => {
      setResentVerification(true)
      setIsEmailVerified(false);
    }

    const onError = () => {
      setIsEmailVerified(true);
    }

    resendEmailVerification({email}, {onSuccess, onError})

  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Auto redirection on being select remember me
  useEffect(() => {
    if (localStorage.getItem('remember_me') === "remember") {
      //navigate('/dashboard');
      return
    }

    localStorage.clear()
  }, []);

  const resetAllErrorsMessage = () => {
    setIsEmailVerified(false);
    setInvalidCredentials(false);
    setUnknownError(false);
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    resetAllErrorsMessage()

    const formData = new FormData(event.currentTarget);
    const emailValue = formData.get('email');
    const passwordValue = formData.get('password');
    const rememberMe = formData.get('remember');

    if (!emailValue || !passwordValue) {
      return
    }

    const onSuccess = (response: any) => {
      if (response.code === 200) {
        localStorage.setItem('id', response.data.id);
        localStorage.setItem('user', response.data.user);
        localStorage.setItem('email', response.data.email)
        localStorage.setItem('is_admin', response.data.is_admin)
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem("is_user_logged_in", "true");

        if (typeof rememberMe === "string") {
          localStorage.setItem("remember_me", rememberMe);
        } else {
          localStorage.removeItem('remember_me');
        }

        navigate('/dashboard');
      }
    }

    const onError = (error: any) => {
      if (error?.response?.data?.code === 409) {
        setIsEmailVerified(true)
        return
      }

      if (error?.response?.data?.code === 403) {
        setInvalidCredentials(true)
        return;
      }

      setUnknownError(true)
      return;
    }

    if (typeof emailValue === 'string' && typeof passwordValue === 'string') {

      setEmail(emailValue);

      signIn({ email: emailValue, password: passwordValue }, {onSuccess, onError});
    }

  };

  const validateInputs = () => {
    const email = document.getElementById('email') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleGoogleLogin = () => {
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_AUTH_REDIRECTION_URI}&response_type=code&scope=email%20profile`;
  };

  // const handleFacebookLogin = () => {
  //   window.location.href = `https://www.facebook.com/v10.0/dialog/oauth?client_id=${process.env.REACT_APP_FACEBOOK_CLIENT_ID}&redirect_uri=${process.env.REACT_AUTH_REDIRECTION_URI}&state={state-param}`;
  // };


  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <CustomContainer direction="column" justifyContent="space-between" >
        <CustomCard variant="outlined">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={McdcLogo}
              alt="McdcLogo"
              style={{
                width: "auto",
                height: "auto",
                maxWidth: "120px",
                maxHeight: "120px",
                objectFit: "contain",
              }}
            />
            <img
              src={ClemsonLogo}
              alt="ClemsonLogo"
              style={{
                width: "auto",
                height: "auto",
                maxWidth: "50px",
                maxHeight: "50px",
                objectFit: "contain",
                // marginBottom: "16px"
              }}
            />
          </div>
          <Typography
            component="h1"
            variant="h4"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              fontSize: "clamp(1rem, 4vw, 2rem)",
              padding: 0,
              marginTop: 0,
            }}
          >
            Sign in
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleLogin}
              startIcon={<GoogleIcon />}
            >
              Sign in with Google
            </Button>
            {/*<Button*/}
            {/*  fullWidth*/}
            {/*  variant="outlined"*/}
            {/*  onClick={() => alert('Sign in with Facebook')}*/}
            {/*  startIcon={<FacebookIcon />}*/}
            {/*  className="p-1"*/}
            {/*>*/}
            {/*  Sign in with Facebook*/}
            {/*</Button>*/}
          </Box>
          <Divider>Or</Divider>

          {/* unknown error. */}
          {resentVerification && (
            <Stack sx={{ width: '100%' }} spacing={2}>
              <Alert severity="success" className={"bg-green-900"}>
                Verification email sent. Please check your inbox, or spam/junk folder, to verify your account.
              </Alert>
            </Stack>
          )}

          {/* unknown error. */}
          {unknownError && (
            <Stack sx={{ width: '100%' }} spacing={2}>
              <Alert severity="error">
                An unexpected error occurred. For any assistance, you can reach out to us at {" "}
                <a href="mailto:mcdcadmin@clemson.edu" style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 'bold' }}>
                  macdcadmin@clemson.edu
                </a>.
              </Alert>
            </Stack>
          )}

          {/* invalid creds. */}
          {invalidCredentials && (
            <Stack sx={{ width: '100%' }} spacing={2}>
              <Alert severity="error" className={"bg-red-900"}>
                Invalid username or password. Please try again.
              </Alert>
            </Stack>
          )}

          {/* If user’s email is unverified, show a fancy alert. */}
          {isEmailVerified && (
            <Stack sx={{ width: '100%' }} spacing={2}>
              <Alert severity="warning">
                It looks like you haven't verified your email address yet. Didn’t receive it?{' '}
                <Link component="button"
                      onClick={handleResendVerificationEmail}
                >
                  Send
                </Link>
              </Alert>
            </Stack>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2
            }}
          >
            <FormControl>
              <FormLabel htmlFor="email">Email</FormLabel>
              <TextField
                error={emailError}
                helperText={emailErrorMessage}
                id="email"
                type="email"
                name="email"
                placeholder="your@email.com"
                autoComplete="email"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={emailError ? 'error' : 'primary'}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password">Password</FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="••••••"
                type="password"
                id="password"
                autoComplete="current-password"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={passwordError ? 'error' : 'primary'}
              />
            </FormControl>
            <FormControlLabel
              control={<Checkbox value="remember" color="primary" defaultChecked={true}/>}
              label="Remember me"
              name="remember"
            />
            <ForgotPassword open={open} handleClose={handleClose} />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              onClick={validateInputs}
            >
              {isSigning ? "Signing..." : "Sign in"}
            </Button>
            <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              variant="body2"
              sx={{ alignSelf: 'center' }}
            >
              Forgot your password?
            </Link>
          </Box>
          <Divider></Divider>
          <Box sx={{ display: 'flex', flexDirection: 'col', gap: 2, justifyContent: 'center' }}>
            <Typography sx={{ textAlign: 'center' }}>
              Don&apos;t have an account?{' '}
              <Link
                onClick={() => navigate("/signup")}
                variant="body2"
                sx={{ alignSelf: 'center', cursor:'pointer' }}
              >
                Sign up
              </Link>
            </Typography>
          </Box>
        </CustomCard>
      </CustomContainer>
    </AppTheme>
  );
}
